/* eslint-disable react/prop-types */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import React, { useState } from 'react';
import { Button, Form, Row, Col } from 'react-bootstrap';
import Flex from '../Flex';

export const ServerSideTableFooter = ({
  page,
  pageSize,
  pageIndex,
  rowCount,
  paginationFilter,
  setPageSize,
  canPreviousPage,
  canNextPage,
  viewAllBtn,
  nextPage,
  previousPage,
  rowInfo,
  perPage,
  rowsPerPageSelection,
  navButtons,
  rowsPerPageOptions = [5, 10, 20, 25],
  className,
  onPageChange, // New callback prop for page change
  onPageSizeChange, // New callback prop for page size change
  hideCount
}) => {
  const [isAllVisible, setIsAllVisible] = useState(false);

  const handlePageSizeChange = e => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    onPageSizeChange(newSize); // Trigger parent callback
  };

  const pageCount = Math.ceil(rowCount / pageSize);
  const totalPages = pageCount || 1;

  // Function to get 3 visible page numbers
  const getVisiblePages = () => {
    const currentPage = pageIndex + 1;
    const pages = [];

    if (totalPages <= 3) {
      // If total pages are 3 or less, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else if (currentPage === 1) {
      // At first page
      pages.push(1, 2, totalPages);
    } else if (currentPage === totalPages) {
      // At last page
      pages.push(1, totalPages - 1, totalPages);
    } else {
      // In the middle - show current page and neighbors
      pages.push(currentPage - 1, currentPage, currentPage + 1);
    }

    return pages;
  };

  const handlePageChange = page => {
    if (page >= 0 && page < totalPages) {
      onPageChange(page);
    }
  };

  // Info section component
  const InfoSection = () => (
    <Flex alignItems="center" className="fs--1">
      {!rowInfo && !hideCount && (
        <p className="mb-0">
          <span className="d-none d-sm-inline-block me-2">
            Showing{' '}
            {paginationFilter
              ? rowCount != 0
                ? 1
                : 0
              : rowCount != 0
              ? pageIndex * pageSize + 1
              : 0}{' '}
            to{' '}
            {rowCount < pageIndex * pageSize + pageSize
              ? rowCount
              : pageIndex * pageSize + pageSize || rowCount}{' '}
            of {rowCount}
          </span>
          {viewAllBtn && (
            <>
              <span className="d-none d-sm-inline-block me-2">&mdash;</span>
              <Button
                variant="link"
                size="sm"
                className="py-2 px-0 fw-semi-bold"
                onClick={() => {
                  setIsAllVisible(!isAllVisible);
                  setPageSize(isAllVisible ? perPage : rowCount);
                }}
              >
                View {isAllVisible ? 'less' : 'all'}
                <FontAwesomeIcon icon="chevron-right" className="ms-1 fs--2" />
              </Button>
            </>
          )}
        </p>
      )}
    </Flex>
  );

  // Rows per page section component
  const RowsPerPageSection = () =>
    rowsPerPageSelection &&
    !hideCount && (
      <Flex alignItems="center" className="fs--1">
        <p className="mb-0 me-2">Rows per page:</p>
        <Form.Select
          size="sm"
          className="w-auto"
          onChange={handlePageSizeChange}
          defaultValue={pageSize}
        >
          {rowsPerPageOptions.map(value => (
            <option value={value} key={value}>
              {value}
            </option>
          ))}
        </Form.Select>
      </Flex>
    );

  // Navigation section component
  const NavigationSection = () =>
    navButtons && (
      <Flex>
        {/* Previous Arrow Button */}
        <Button
          size="sm"
          variant="light"
          onClick={() => handlePageChange(pageIndex - 1)}
          className={classNames('px-4 ms-2', {
            disabled: !canPreviousPage
          })}
        >
          &lt;
        </Button>

        {/* Always show exactly 3 numbers */}
        {getVisiblePages().map((page, index) => (
          <Button
            key={index}
            size="sm"
            variant={page === pageIndex + 1 ? 'primary' : 'light'}
            onClick={() => handlePageChange(page - 1)}
            className="px-3"
          >
            {page}
          </Button>
        ))}

        {/* Next Arrow Button */}
        <Button
          size="sm"
          variant="light"
          onClick={() => handlePageChange(pageIndex + 1)}
          className={classNames('px-4 ms-2', {
            disabled: !canNextPage
          })}
        >
          &gt;
        </Button>
      </Flex>
    );

  return (
    <div className={classNames(className)}>
      {/* Large screens - single row layout */}
      <div className="d-none d-md-flex align-items-center justify-content-between">
        <Flex alignItems="center">
          <InfoSection />
          <RowsPerPageSection />
        </Flex>
        <NavigationSection />
      </div>

      {/* Small screens - stacked layout */}
      <div className="d-md-none">
        <Row className="g-2">
          {/* Navigation on top for small screens */}
          {navButtons && (
            <Col xs={12} className="d-flex justify-content-center">
              <NavigationSection />
            </Col>
          )}

          {/* Info section */}
          {!rowInfo && !hideCount && (
            <Col xs={12} className="d-flex justify-content-center">
              <InfoSection />
            </Col>
          )}

          {/* Rows per page on bottom for small screens */}
          {rowsPerPageSelection && !hideCount && (
            <Col xs={12} className="d-flex justify-content-center">
              <RowsPerPageSection />
            </Col>
          )}
        </Row>
      </div>
    </div>
  );
};

export default ServerSideTableFooter;
